import { ForwardChevrons, X } from '@griply/assets';
import { isAfter, isBefore, isSameDay, isSameMonth } from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { DayContextMenu } from 'shared/components/ui/day-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { useToday } from 'shared/contexts/today';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { isBetween } from 'shared/lib/date-fns';
import {
  ScheduleEntryState,
  ScheduleTargetEntry,
} from 'shared/types/habit-schedule';
import { Timeframe } from 'shared/types/timeframe';
import { Timestamp } from 'shared/types/timestamp';
import { useTheme } from 'styled-components';

import * as Styled from './habit-progress-calendar.style';

export type DayProps = {
  date: Timestamp;
  monthCompareDate: Timestamp;
  occurrences: Timestamp[];
  dateEntries: ScheduleTargetEntry[];
  perfectSlots?: Timeframe[];
  scheduleChanges?: { date: Timestamp }[];
  onDate?: (
    date: Timestamp,
    type: ScheduleEntryState,
    options?: { value?: number; increment?: number },
  ) => void;
  onDateManual: (date: Timestamp) => void;
  showDate?: boolean;
  showPrevMonthDate?: boolean;
  showNextMonthDate?: boolean;
  isDataTaskType?: boolean;
};

export const Day: React.FC<DayProps> = ({
  date,
  monthCompareDate,
  occurrences,
  dateEntries,
  perfectSlots = [],
  scheduleChanges = [],
  onDate,
  onDateManual,
  showDate,
  showPrevMonthDate,
  showNextMonthDate,
  isDataTaskType,
}) => {
  const today = useToday();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const { position, open, close } = useContextMenuState({
    disabled: theme.isMobile,
  });

  const dateEntry = useMemo(
    () => dateEntries.find((entry) => isSameDay(entry.date, date)),
    [date, dateEntries],
  );
  const completionPercentage = dateEntry?.value
    ? (dateEntry.value / (dateEntry.target ?? 0)) * 100
    : 0;
  const isFailure = dateEntry?.state === ScheduleEntryState.Failure;
  const isSkipped =
    dateEntry?.state === ScheduleEntryState.Skip && !dateEntry?.hidden;
  const isFuture = isAfter(date, today);

  const isPrevMonth = useMemo(
    () =>
      !isSameMonth(date, monthCompareDate) && isBefore(date, monthCompareDate),
    [date, monthCompareDate],
  );

  const isNextMonth = useMemo(
    () =>
      !isSameMonth(date, monthCompareDate) && isAfter(date, monthCompareDate),
    [date, monthCompareDate],
  );

  const isPerfectDay = useMemo(
    () =>
      !!perfectSlots.find(({ startDate, endDate }) =>
        isBetween(date, startDate, endDate),
      ),
    [date, perfectSlots],
  );

  const isScheduleChange = useMemo(
    () =>
      !!scheduleChanges.find((scheduleChange) =>
        isSameDay(scheduleChange.date, date),
      ),
    [date, scheduleChanges],
  );

  const onComplete = onDate
    ? () => {
        dateEntry?.onDateManual
          ? onDateManual(date)
          : onDate(date, ScheduleEntryState.Complete);
        close();
      }
    : undefined;

  const isOccurrence = useMemo(
    () =>
      !dateEntry?.hidden &&
      !!occurrences.find((occurrence) => isSameDay(occurrence, date)),
    [date, occurrences, dateEntry],
  );

  const onManualLogging = () => onDateManual(date);

  const openMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    !isFuture && open(e);
  };

  if (
    (!showPrevMonthDate && isPrevMonth) ||
    (!showNextMonthDate && isNextMonth)
  ) {
    return null;
  }

  return (
    <Styled.DayContainer ref={containerRef} onContextMenu={openMenu}>
      <Styled.Day
        $isToday={isSameDay(date, today)}
        $isOccurrence={isOccurrence}
        $completionPercentage={completionPercentage}
        $isPerfectDay={isPerfectDay}
        $isSkipped={isSkipped}
        $isFailure={isFailure}
        $isFuture={isFuture}
        $showDate={!!showDate}
        onClick={onComplete}
      >
        {!completionPercentage && (isSkipped || isFailure) ? (
          <Styled.DayIcon>
            <Icon icon={isSkipped ? ForwardChevrons : X} strokeWidth={2.5} />
          </Styled.DayIcon>
        ) : (
          showDate && <Styled.DayLabel>{date.getDate()}</Styled.DayLabel>
        )}
      </Styled.Day>
      {isScheduleChange && <Styled.RescheduleDot />}
      {!!dateEntry?.showValueAsBadge && !!dateEntry.value && (
        <Styled.ValueBadge value={dateEntry.value} />
      )}

      {!!position && !!onDate && (
        <DayContextMenu
          date={date}
          onDate={onDate}
          isDataTaskType={isDataTaskType}
          target={dateEntry?.target ?? 1}
          onManual={onManualLogging}
          referenceElement={containerRef}
          onClose={close}
        />
      )}
    </Styled.DayContainer>
  );
};
